import React, { useState, useLayoutEffect } from 'react'
import Modal from 'react-overlays/Modal'
import Spinner from './Spinner'
import { Swiper, SwiperSlide } from 'swiper/react'
import { EffectFade, Autoplay, Navigation, Pagination } from 'swiper/modules'
import SwiperCore from 'swiper'
import {
  CircularProgressbarWithChildren,
  buildStyles,
} from 'react-circular-progressbar'
import 'swiper/css/bundle'
import 'swiper/css'
import { cn } from '@/lib/utils'

import {
  MapProvider,
  Map,
  Source,
  Layer,
  Marker,
} from 'react-map-gl/maplibre'
import { toast } from 'react-toastify'
import { useRef } from 'react'
import maplibregl from 'maplibre-gl'
import { geojsonToWKT } from '@terraformer/wkt'
import { callAPI } from '../hooks/api'
import FloodData from './FloodData'
import SoilData from './SoilData'
import CropData from './CropData'
import SoilProperties from './SoilProperties'
import RanchSelectModal from './RanchSelectModal'
import Watershed from './Watershed'
import DeletePopupModal from './DeletePopupModal'
import WeatherHistory from './WeatherHistory'
import FeedbackPopupModal from './FeedbackPopupModal'
import useAbility from '@/hooks/use-ability'
import UpgradeLayout from './upgrade-layout'
import UpgradeLockIconLayout from './upgrade-lock-icon-layout'
import { undefinedToZero } from '@/hooks/utils'

export default function MobileParcelDetail({
  showModal,
  handleClose,
  property,
  logout,
  localAuth,
  selectedRanch,
  setSelectedRanch,
  drawParcelsByRanchId,
  onParcelDelete,
  ranches,
  setRanches,
  onRanchParcelDelete,
}) {
  const isDemoUser =
    localAuth.sessionKey === 'a6cf9f6e-3341-4996-8d6a-83e826b67adf'
  const [feedbackPopupModal, setFeedbackPopupModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [stateDistricts, setStateDistricts] = useState({
    'State Assembly District': '0',
    'State Senate District': '0',
  })
  const [floodData, setFloodData] = useState([])

  const [deletePopupModal, setDeletePopupModal] = useState(false)
  const [watershed, setWatershed] = useState({
    watershed: [],
    watershed_county: [],
    watershed_wd: [],
    watershed_gsa: [],
    watershed_hydrologic_region: [],
  })
  const [soilData, setSoilData] = useState([])
  const [soilProperties, setSoilProperties] = useState([])
  const [showRanchSelectModal, setShowRanchSelectModal] = useState(false)

  const mapRef = useRef(null)
  const [selectedParcelIds, setSelectedParcelIds] = useState([])
  const [ranchName, setRanchName] = useState('')

  function onChange(e) {
    setRanchName(e.target.value)
  }

  const onRanchDropdownChange = (e) => {
    const ranch = ranches.find((item) => item.ranch_name === e.value)
    setSelectedRanch(ranch)
  }

  function addParcelToRanch(ranch) {
    // on every click push a new coordinate to the boxes array
    const {
      properties: {
        parcel_id,
        owner,
        address,
        mail_address,
        county_name,
        mkt_val_land,
        mkt_val_bldg,
        hs,
        ss,
        latitude,
        longitude,
        gsa_info,
        wd_info,
        acreage_calc,
        crop_composition,
        sv,
        soil_composition,
        fld_zone,
        main_crop,
        hydrologic_info,
        acreage_deeded,
      },
      ...remainingFeature
    } = property.feature

    const transformedFeature = {
      ...remainingFeature,
      properties: {
        parcel_id,
        owner,
        address,
        mail_address,
        county_name,
        mkt_val_land,
        mkt_val_bldg,
        hs,
        ss,
        latitude,
        longitude,
        gsa_info,
        wd_info,
        acreage_calc,
        crop_composition,
        sv,
        soil_composition,
        fld_zone,
        main_crop,
        hydrologic_info,
        acreage_deeded,
      },
    }
    transformedFeature.properties['mkt_val_land'] = undefinedToZero(
      transformedFeature.properties['mkt_val_land']
    )
    transformedFeature.properties['mkt_val_bldg'] = undefinedToZero(
      transformedFeature.properties['mkt_val_bldg']
    )
    
    if ('sv' in transformedFeature.properties) {
      const base64Image = btoa(transformedFeature.properties['sv'])
      transformedFeature.properties['sv'] =
        `data:image/png;base64,${base64Image}`
    }
    transformedFeature.properties['soil_properties'] = soilProperties

    var endpoint = '/add_parcel_to_ranch'
    var params = {
      geojson_str: JSON.stringify(transformedFeature),
      ranch_id: ranch.ranch_id,
      session_key: localAuth.sessionKey,
    }
    setShowRanchSelectModal(false)
    setLoading(true)
    callAPI(endpoint, params)
      .then((res) => {
        setLoading(false)
        if (onParcelDelete) {
          onParcelDelete(property.id)
        }
        handleClose()
        setSelectedRanch(ranch)
        drawParcelsByRanchId(ranch.ranch_id)
      })
      .catch((e) => {
        setLoading(false)
      })
  }

  function createRanches(add = false) {
    setLoading(true)
    var endpoint = '/create_ranch'
    var params = {
      ranch_name: ranchName,
      session_key: localAuth.sessionKey,
    }
    callAPI(endpoint, params).then((res) => {
      setLoading(false)
      if (res.status === 'fail') {
        toast.warn(res.msg)
        return
      } else if (res.status === 'expire') {
        toast.warn(res.msg)
        return
      }

      invalidateQuery()
      setRanches(res.ranches.slice().sort((a, b) => b.ranch_id - a.ranch_id))
      const ranch = res.ranches.find(
        (item) => item.ranch_name === res.ranch_name
      )
      setRanchName('')
      if (add) addParcelToRanch(ranch)
      else setSelectedRanch(ranch)
    })
  }

  async function onSubmit(e, add = false) {
    e.preventDefault()
    if (ranchName === '') {
      toast.warn('Must type ranch name first.')
      return
    } else {
      try {
        createRanches(add)
      } catch (error) {
        toast.warn(error.message)
      }
    }
  }

  const layerStyle = {
    id: 'Google Satellite Hybrid',
    type: 'raster',
    source: 'google_earth_hybrid',
    layout: { visibility: 'visible' },
  }

  function hydraScoreColor(hydraScore) {
    if (hydraScore >= 93) return '#008000'
    else if (hydraScore >= 79) return '#8FB935'
    else if (hydraScore >= 68) return '#E6E22E'
    else if (hydraScore >= 48) return '#E09C3B'
    else if (hydraScore === 'NR') return '#808080'
    else return '#E64747'
  }

  useLayoutEffect(() => {
    async function getStateDistricts() {
      var lat = property.data.geolocation.lat
      var lon = property.data.geolocation.lng

      var endpoint = '/get_state_districts'
      var params = { lat: lat, lon: lon, session_key: localAuth.sessionKey }

      var res = await callAPI(endpoint, params)
      if (res.status === 'success') {
        setStateDistricts(res.state_districts)
      } else if (res.status === 'fail') {
        toast.warn(res.msg)
        return
      } else if (res.status === 'expire') {
        toast.warn(res.msg)
        setLoading(false)
        logout()
        return
      }
    }
    getStateDistricts()
  }, [])

  const renderBackdrop = (props) => <div className="backdrop" {...props} />
  SwiperCore.use([Autoplay, Navigation, Pagination])

  const addToPortfolioButtonClick = () => {
    setShowRanchSelectModal(true)
    setSelectedParcelIds([property.id])
  }

  const getZoomLevel = () => {
    if (parseInt(property.data.acre) > 10000) {
      return 10
    } else if (parseInt(property.data.acre) > 5000) {
      return 11
    } else if (parseInt(property.data.acre) > 1000) {
      return 12
    } else if (parseInt(property.data.acre) > 200) {
      return 13
    } else {
      return 14
    }
  }

  const {
    checkAbility,
    invalidateQuery,
  } = useAbility()

  const enableOwnershipInformation =
    checkAbility('ownership_information') || isDemoUser
  const enableHydraScore = checkAbility('hydra_score')
  const enableSoilScore = checkAbility('soil_score')
  const enableFloodMap = checkAbility('flood_maps')
  const enableSoilTypes = checkAbility('soil_types')
  const enableSoilProperties = checkAbility('soil_properties')
  const enableApnDisplay = checkAbility('apn_display')
  const enableAssemblyAndSenateDistricts = checkAbility(
    'assembly_and_senate_district'
  )
  const enableHistoricalClimateData = checkAbility('historical_climate_data')
  const enablePropertyLines = checkAbility('property_lines') || isDemoUser
  const enableCustomRanches = checkAbility('custom_ranches_portfolio')

  if (loading) {
    return <Spinner />
  } else {
    return (
      <>
        {feedbackPopupModal && (
          <FeedbackPopupModal
            setFeedbackPopupModal={setFeedbackPopupModal}
            feedbackPopupModal={feedbackPopupModal}
            property={property}
            setLoading={setLoading}
          />
        )}

        {deletePopupModal && (
          <DeletePopupModal
            setDeletePopupModal={setDeletePopupModal}
            deletePopupModal={deletePopupModal}
            onDelete={onRanchParcelDelete}
            type={'Parcel'}
          />
        )}
        <div className="modal-example">
          <Modal
            className="modal"
            show={showModal}
            onHide={handleClose}
            renderBackdrop={renderBackdrop}
            style={{
              alignItems: 'center',
              justifyContent: 'center',
              height: '100vh',
            }}
          >
            <div id="single-parcel-all" className="max-w-6xl px-3 mt-6 mx-auto">
              {showRanchSelectModal && (
                <RanchSelectModal
                  selectedRanch={selectedRanch}
                  onChange={onChange}
                  ranchName={ranchName}
                  onSubmit={(e) => onSubmit(e, true)}
                  setSelectedParcelIds={setSelectedParcelIds}
                  showRanchSelectModal={showRanchSelectModal}
                  setShowRanchSelectModal={setShowRanchSelectModal}
                  onRanchDropdownChange={onRanchDropdownChange}
                  ranches={ranches}
                  setRanches={setRanches}
                  addParcelToRanch={addParcelToRanch}
                />
              )}
              <div id="single-parcel-header" className="modal-header">
                <div className="relative w-full mb-3">
                  <UpgradeLockIconLayout
                    className="h-full ml-[180px] w-[30px]"
                    iconSize={{ width: 24, height: 24 }}
                    show={!enableOwnershipInformation}
                  />
                  <p className="text-2xl font-bold text-blue-900">
                    Property Detail
                    <span className={`${isDemoUser ? 'blur' : ''}`}>
                      {enableOwnershipInformation
                        ? `(${property.data.name})`
                        : property.data.name}
                    </span>
                  </p>
                </div>
                <div>
                  <span className="close-button" onClick={handleClose}>
                    x
                  </span>
                </div>
              </div>
              <div
                id="single-parcel-images"
                className="min-h-[120px] justify-between items-center shadow-md hover:shadow-lg  rounded-md overflow-hidden transition-shadow duration-150 m-[10px]"
              >
                <Swiper
                  slidesPerView={1}
                  navigation
                  pagination={{ type: 'progressbar' }}
                  effect="fade"
                  modules={[EffectFade]}
                  autoplay={{ delay: 3000 }}
                  style={{ zIndex: 1000 }}
                >
                  {property.data.imgUrls.map((url, index) => (
                    <SwiperSlide key={index}>
                      <div
                        className="relative w-full overflow-hidden h-[300px]"
                        style={{
                          background: `url(${url}) center no-repeat`,
                          backgroundSize: 'cover',
                        }}
                      ></div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
              <div
                id="single-parcel-dashboard"
                className="flex min-h-[120px] shadow-md hover:shadow-lg  rounded-md overflow-hidden transition-shadow duration-150 columns-6 justify-between items-center m-[10px] p-[10px]"
              >
                <div className="text-center w-full content-center">
                  <div className="flex-col">
                    <h2 className="underline">Hydra Score</h2>
                    <div className="w-full flex flex-row justify-center items-center">
                      <div
                        className="w-[70px] relative"
                        title={property.feature.properties.hover}
                      >
                        <UpgradeLockIconLayout
                          className="h-full rounded-full items-center justify-center opacity-80"
                          iconSize={{ width: 24, height: 24 }}
                          show={!enableHydraScore}
                        />
                        {property.data.hydraScore && (
                          <CircularProgressbarWithChildren
                            value={property.data.hydraScore}
                            background
                            backgroundPadding={6}
                            styles={buildStyles({
                              backgroundColor: hydraScoreColor(
                                property.data.hydraScore
                              ),
                              pathColor: '#fff',
                              trailColor: 'transparent',
                            })}
                          >
                            <div className="text-white" style={{ fontSize: 9 }}>
                              Hydra
                            </div>
                            <strong
                              className="text-white text-xs"
                              style={{ fontSize: 25 }}
                            >
                              <span className={`${isDemoUser ? 'blur' : ''}`}>
                                {property.data.hydraScore}
                              </span>
                            </strong>
                            <div className="text-white" style={{ fontSize: 9 }}>
                              Score
                            </div>
                          </CircularProgressbarWithChildren>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="text-center w-full content-center">
                  <div className="flex-col">
                    <h2 className="underline">Soil Score</h2>
                    <div className="w-full flex flex-row justify-center items-center">
                      <div className="w-[70px] relative">
                        <UpgradeLockIconLayout
                          className="h-full rounded-full items-center justify-center opacity-80"
                          iconSize={{ width: 24, height: 24 }}
                          show={!enableSoilScore}
                        />
                        {property.data.ss && (
                          <CircularProgressbarWithChildren
                            value={property.data.ss}
                            background
                            backgroundPadding={6}
                            styles={buildStyles({
                              backgroundColor: hydraScoreColor(
                                property.data.ss
                              ),
                              pathColor: '#fff',
                              trailColor: 'transparent',
                            })}
                          >
                            <div className="text-white" style={{ fontSize: 9 }}>
                              Soil
                            </div>
                            <strong
                              className="text-white text-xs"
                              style={{ fontSize: 25 }}
                            >
                              <span className={`${isDemoUser ? 'blur' : ''}`}>
                                {property.data.ss}
                              </span>
                            </strong>
                            <div className="text-white" style={{ fontSize: 9 }}>
                              Score
                            </div>
                          </CircularProgressbarWithChildren>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div
                id="single-parcel-crop-data"
                className="min-h-[120px] justify-between items-center shadow-md hover:shadow-lg  rounded-md overflow-hidden transition-shadow duration-150 m-[10px]"
              >
                <div>
                  <h2 className="underline ml-[20px] mt-[20px]">Crop Data:</h2>
                    <CropData
                      cropData={property.feature.properties.crop_composition}
                      isDemoUser={isDemoUser}
                    />
                </div>
              </div>
              <div id="single-parcel-buttons" className="sticky top-0 z-[100]">
                <div className="flex min-h-[120px] bg-white  rounded-md overflow-hidden  justify-between items-center">
                  <div className="text-center w-full content-center">
                    <div className="relative">
                      <UpgradeLockIconLayout
                        position={'w-fit -left-1 top-0'}
                        className="min-h-4 bg-transparent px-3"
                        iconSize={{ width: 24, height: 24 }}
                        show={!enableCustomRanches}
                      />
                      {property.type === 'ranch' ? (
                        <button
                          disabled={!enableCustomRanches}
                          onClick={() => {
                            if (document.getElementById('spinner')) {
                              toast.warn(
                                'Please wait for all queries to finish first.'
                              )
                              return
                            }
                            setDeletePopupModal(true)
                          }}
                          className="px-7 py-3 bg-red-600
                          
                           disabled:cursor-not-allowed disabled:opacity-50
                          text-white font-medium text-sm uppercase rounded shadow-md hover:bg-red-700 hover:shadow-lg focus:bg-red-700 focus:shadow-lg w-[80%] text-center transition duration-150 ease-in-out btn btn-custom delete-parcel"
                        >
                          Delete
                        </button>
                      ) : (
                        <button
                          disabled={!enableCustomRanches}
                          onClick={() => {
                            if (document.getElementById('spinner')) {
                              toast.warn(
                                'Please wait for all queries to finish first.'
                              )
                              return
                            }
                            addToPortfolioButtonClick()
                          }}
                          className="
                          disabled:cursor-not-allowed disabled:opacity-50
                          px-7 py-3 bg-blue-600 text-white font-medium text-sm uppercase rounded shadow-md hover:bg-blue-700 hover:shadow-lg focus:bg-blue-700 focus:shadow-lg w-[80%] text-center transition duration-150 ease-in-out btn btn-custom add-to-ranch"
                        >
                          Add
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="text-center w-full content-center">
                    <div className="">
                      {enableOwnershipInformation && (
                        <button
                          onClick={() => {
                            if (document.getElementById('spinner')) {
                              toast.warn(
                                'Please wait for all queries to finish first.'
                              )
                              return
                            }
                            navigator.share({
                              title: 'Hydra score parcel(s)',
                              text: 'Click link to see shared parcel(s)',
                              url: `https://app.hydrascore.com/?locations=${btoa(JSON.stringify([{ lat: property.data.geolocation.lat, lon: property.data.geolocation.lng }]))}`,
                            })
                          }}
                          className="px-7 py-3 bg-primary text-white font-medium text-sm uppercase rounded shadow-md hover:bg-[#01589d] hover:shadow-lg focus:bg-[#01589d] focus:shadow-lg w-[80%] text-center transition duration-150 ease-in-out btn btn-custom"
                        >
                          Share
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div
                id="single-parcel-admin"
                className="min-h-[120px] shadow-md hover:shadow-lg  rounded-md overflow-hidden transition-shadow duration-150 m-[10px]"
              >
                <div className="w-full">
                  <h2 className="underline ml-[20px] mt-[20px]">
                    Property APN:
                  </h2>
                  <ul className="flex items-center space-x-2 sm:space-x-10 text-sm font-semibold mb-[10px]">
                    <li className="flex items-center whitespace-wrap">
                      <div className="relative">
                        <UpgradeLockIconLayout
                          className="absolute min-w-[60px] justify-center"
                          show={!enableApnDisplay}
                        />
                        <p className="font-semibold ml-[30px] ">
                          <span className={`${isDemoUser ? 'blur' : ''}`}>
                            {property.data.apn}
                          </span>
                        </p>
                      </div>
                    </li>
                  </ul>
                </div>
                <div className="w-full">
                  <h2 className="underline ml-[20px] mt-[20px]">Acres:</h2>
                  <ul className="flex items-center space-x-2 sm:space-x-10 text-sm font-semibold mb-[10px]">
                    <li className="flex items-center whitespace-wrap">
                      <p className="font-semibold ml-[30px] ">
                        <span className={`${isDemoUser ? 'blur' : ''}`}>
                          {property.feature.properties.hasOwnProperty(
                            'acreage_calc'
                          )
                            ? parseFloat(
                                property.feature.properties.acreage_calc
                              ).toFixed(2)
                            : property.feature.properties['Total Lot Size']}
                        </span>
                      </p>
                    </li>
                  </ul>
                </div>
                <div>
                  <Watershed
                    watershed={watershed}
                    isDemoUser={isDemoUser}
                  />
                </div>
                <div className="w-full">
                  <h2 className="underline ml-[20px] mt-[20px]">
                    Mailing Address:
                  </h2>
                  <ul className="flex items-center space-x-2 sm:space-x-10 text-sm font-semibold mb-[10px]">
                    <li className="flex items-center whitespace-wrap">
                      <div className="relative">
                        <UpgradeLockIconLayout
                          className="absolute min-w-[60px] justify-center"
                          show={!enableOwnershipInformation}
                        />
                        <p className="font-semibold ml-[30px] ">
                          <span className={`${isDemoUser ? 'blur' : ''}`}>
                            {property.feature.properties.mail_address}
                          </span>
                        </p>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
              <div
                id="single-parcel-mapview"
                className="flex flex-col md:flex-row max-w-6xl p-4 min-h-[120px] shadow-md hover:shadow-lg  rounded-md overflow-hidden transition-shadow duration-150 columns-5 justify-between items-center m-[10px] lg:space-x-5"
              >
                <div className=" w-full ">
                  <ul className="items-center space-x-2 sm:space-x-10 text-sm font-semibold mb-1">
                    <li className="items-center whitespace-wrap flex flex-row">
                      <p>Owner: </p>
                      <div className="relative">
                        <UpgradeLockIconLayout
                          className="min-h-[20px]"
                          show={!enableOwnershipInformation}
                        />
                        <span className={`${isDemoUser ? 'blur' : ''}`}>
                          {property.feature.properties.owner}
                        </span>
                      </div>
                    </li>
                  </ul>
                  {
                    <ul className="flex items-center space-x-2 sm:space-x-10 text-sm font-semibold mb-1">
                      <li className="flex items-center whitespace-wrap">
                        Acres:
                        <span className={`${isDemoUser ? 'blur' : ''}`}>
                          {' '}
                          {property.feature.properties.hasOwnProperty(
                            'acreage_calc'
                          )
                            ? parseFloat(
                                property.feature.properties.acreage_calc
                              ).toFixed(2)
                            : ''}
                        </span>
                      </li>
                    </ul>
                  }
                  {property.feature.properties.county ||
                    (property.feature.properties.county_name && (
                      <ul className="flex items-center space-x-2 sm:space-x-10 text-sm font-semibold mb-1">
                        <li className="flex items-center whitespace-wrap">
                          County:{' '}
                          <span className={`${isDemoUser ? 'blur' : ''}`}>
                            {property.feature.properties.county_name}
                          </span>
                        </li>
                      </ul>
                    ))}
                  <div
                    className={`relative ${
                      !enableAssemblyAndSenateDistricts ? 'mt-6' : ''
                    }`}
                  >
                    <UpgradeLayout
                      className="flex flex-row"
                      needUpgrade={!enableAssemblyAndSenateDistricts}
                      title="Upgrade to view Assembly and Senate Districts."
                    />
                    {Object.entries(stateDistricts).map(([key, value]) => (
                      <ul
                        key={key}
                        className="flex items-center space-x-2 sm:space-x-10 text-sm font-semibold mb-1"
                      >
                        <li className="flex items-center whitespace-wrap">
                          {key}:{' '}
                          <span className={`${isDemoUser ? 'blur' : ''}`}>
                            {value}
                          </span>
                        </li>
                      </ul>
                    ))}
                  </div>
                </div>
                <div className="w-full h-[200px] md:h-[400px] z-10 overflow-x-hidden mt-6 md:mt-0 md:ml-2">
                  <MapProvider>
                    <Map
                      id="aerial-view"
                      ref={mapRef}
                      initialViewState={{
                        longitude: property.data.geolocation.lng,
                        latitude: property.data.geolocation.lat,
                        zoom: getZoomLevel(),
                      }}
                      scrollZoom={false}
                      dragPan={false}
                      doubleClickZoom={false}
                      style={{ height: '100%', width: '100%' }}
                      preserveDrawingBuffer={true}
                    >
                      <Source
                        id="google_earth_hybrid"
                        type="raster"
                        tiles={[
                          'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
                        ]}
                        tileSize={256}
                      >
                        <Layer {...layerStyle} />
                      </Source>
                      {enablePropertyLines && property.feature ? (
                        <Source
                          id="property-source"
                          type="geojson"
                          data={property.feature}
                        >
                          <Layer
                            {...{
                              source: 'property-source',
                              id: 'property-layer',
                              type: 'fill',
                              layout: { visibility: 'visible' },
                              paint: {
                                'fill-color': '#FFA500',
                                'fill-opacity': 0.2,
                                'fill-outline-color': '#FFA500',
                              },
                            }}
                          />
                          <Layer
                            {...{
                              source: 'property-source',
                              id: 'property-layer-outline',
                              type: 'line',
                              layout: {
                                visibility: 'visible',
                                'line-join': 'round',
                                'line-cap': 'round',
                              },
                              paint: {
                                'line-color': '#FFA500',
                                'line-width': 4,
                              },
                            }}
                          />
                        </Source>
                      ) : (
                        <Marker
                          longitude={property.data.geolocation.lng}
                          latitude={property.data.geolocation.lat}
                          popup={new maplibregl.Popup().setText(
                            property.data.address
                          )}
                        ></Marker>
                      )}
                    </Map>
                  </MapProvider>
                </div>
              </div>
              <div
                id="single-parcel-soil-type"
                className="min-h-[120px] justify-between items-center shadow-md hover:shadow-lg  rounded-md overflow-hidden transition-shadow duration-150 m-[10px]"
              >
                <div>
                  <h2 className="underline ml-[20px] mt-[20px]">
                    Soil Types:{' '}
                  </h2>
                  <div className="relative">
                    <UpgradeLayout
                      title="Upgrade to view Soil Types."
                      needUpgrade={!enableSoilTypes}
                    />
                      <SoilData
                        soilData={property.feature.properties.soil_composition}
                        localAuth={localAuth}
                        isDemoUser={isDemoUser}
                      />
                  </div>
                </div>
              </div>
              {/* <div
                id="single-parcel-soil-properties"
                className={cn(
                  'min-h-[120px] max-h-[400px] justify-between items-center shadow-md hover:shadow-lg rounded-md overflow-y-auto transition-shadow duration-150 m-[10px]',
                  {
                    'overflow-y-hidden': !enableSoilProperties,
                  }
                )}
              >
                <div>
                  <h2 className="underline ml-[20px] mt-[20px]">
                    Soil Properties:{' '}
                  </h2>
                  {enableSoilProperties ? (
                    <SoilProperties
                      property={property}
                      localAuth={localAuth}
                      setSoilProperties={setSoilProperties}
                      soilProperties={soilProperties}
                      isDemoUser={isDemoUser}
                    />
                  ) : (
                    <div className="w-full h-[120px] relative">
                      <UpgradeLayout
                        className="h-[100px] w-full"
                        title="Upgrade to view Soil Properties."
                        needUpgrade={!enableSoilProperties}
                      />
                    </div>
                  )}
                </div>
              </div> */}
              {!isDemoUser && (
                <div
                  id="single-parcel-flood"
                  className="min-h-[120px] justify-between items-center shadow-md hover:shadow-lg  rounded-md overflow-hidden transition-shadow duration-150 m-[10px]"
                >
                  <div>
                    <h2 className="underline ml-[20px] mt-[20px]">
                      Flood Risk:
                    </h2>
                    <div className="relative">
                      <UpgradeLayout
                        title="Upgrade to view Flood Risk."
                        needUpgrade={!enableFloodMap}
                      />
                        <FloodData
                          setFloodData={setFloodData}
                          wkt={geojsonToWKT(property.feature.geometry)}
                          apn={property.data.apn}
                          lat={property.data.geolocation.lat}
                          lon={property.data.geolocation.lng}
                          localAuth={localAuth}
                        />
                    </div>
                  </div>
                </div>
              )}
              {!isDemoUser ? (
                !enableHistoricalClimateData ? (
                  <div
                    id="single-parcel-weather-history"
                    className="min-h-[120px] justify-between items-center shadow-md hover:shadow-lg  rounded-md overflow-hidden transition-shadow duration-150 m-[10px]"
                  >
                    <div>
                      <h2 className="underline ml-[20px] mt-[20px]">
                        Historical Weather Data (~10 years):
                      </h2>
                      <WeatherHistory
                        lat={property.data.geolocation.lat}
                        lon={property.data.geolocation.lng}
                        localAuth={localAuth}
                      />
                    </div>
                  </div>
                ) : (
                  <div
                    id="single-parcel-weather-history"
                    className="h-[180px] justify-between items-center shadow-md hover:shadow-lg  rounded-md overflow-hidden transition-shadow duration-150 m-[10px] relative"
                  >
                    <UpgradeLayout
                      title="Upgrade to view Historical Climate Data."
                      className="h-[180px]"
                      needUpgrade={!enableHistoricalClimateData}
                    />
                  </div>
                )
              ) : (
                <></>
              )}
              {!isDemoUser && (
                <div
                  id="single-feedback-buttons"
                  className="fixed bottom-0 right-10 z-[100]"
                >
                  <div className="flex overflow-hidden justify-between items-center w-[300px]">
                    <div className="text-center w-full content-center">
                      <div className="relative">
                        <button
                          onClick={() => {
                            setFeedbackPopupModal(true)
                          }}
                          className="px-7 py-3 bg-blue-600 text-white font-medium text-sm uppercase rounded-t-2xl shadow-md hover:bg-blue-700 hover:shadow-lg focus:bg-blue-700 focus:shadow-lg w-full text-center transition duration-150 ease-in-out btn"
                        >
                          Send Us Feedback
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="m-20"></div>
            </div>
          </Modal>
        </div>
      </>
    )
  }
}
